import React, { useState, useRef, useEffect } from "react";
import { BuilderBlocks, Builder } from "@builder.io/react";

interface PromoBannerProps {
  builderBlock?: any;
  sticky?: boolean;
}

const PromoBanner: React.FC<PromoBannerProps> = ({ builderBlock, sticky = false }) => {
  const [isFixed, setIsFixed] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [navbarHeight, setNavbarHeight] = useState(0);

  useEffect(() => {
    if (!sticky) return;

    const updateNavbarHeight = () => {
      const isMobileOrTablet = window.matchMedia("(max-width: 1199px)").matches;
      if (isMobileOrTablet) {
        const navbar = document.querySelector(".fullHeaderBanner");
        setNavbarHeight(navbar ? navbar.getBoundingClientRect().height : 0);
      } else {
        setNavbarHeight(0);
      }
    };

    updateNavbarHeight();

    const navbar = document.querySelector(".fullHeaderBanner");
    if (navbar) {
      const resizeObserver = new ResizeObserver(updateNavbarHeight);
      resizeObserver.observe(navbar);
      return () => resizeObserver.disconnect();
    }
  }, [sticky]);

  useEffect(() => {
    if (!sticky) {
      setIsFixed(false);
      return;
    }

    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: `-${navbarHeight}px 0px 0px 0px` }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [sticky, navbarHeight]);

  useEffect(() => {
    if (sticky && bannerRef.current) {
      setBannerHeight(bannerRef.current.offsetHeight);
    }
  }, [sticky]);

  return (
    <>
      <style>{`
        @keyframes bannerSlideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
      `}</style>
      {sticky && (
        <>
          <div ref={sentinelRef} style={{ height: 0 }} />
          {isFixed && <div style={{ height: `${bannerHeight}px` }} />}
        </>
      )}
      <div
        ref={bannerRef}
        style={{
          width: "100%",
          backgroundColor: "inherit",
          alignContent: "center",
          minHeight: "30px",
          ...(sticky && isFixed
            ? {
                position: "fixed" as const,
                top: navbarHeight,
                left: 0,
                zIndex: 1100,
                animation: "bannerSlideDown 0.5s ease",
              }
            : {}),
        }}
      >
        <BuilderBlocks
          blocks={builderBlock?.children}
          dataPath="children"
          parentElementId={builderBlock?.id}
        />
      </div>
    </>
  );
};

export default PromoBanner;

export const promoBannerRegistration = {
  name: "Promo Banner",
  inputs: [
    {
      name: "sticky",
      type: "boolean",
      defaultValue: false,
      friendlyName: "Sticky",
    },
  ],
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/format-text.svg",
  description: "Add a Promo Banner component to the page",
  canHaveChildren: true,
  defaultChildren: [],
};
